import { PHARMACY_HELP_CATEGORIES } from "./pharmacyHelp.js";
import { clinicalOutputsHelp } from "./clinicalOutputsHelp.js";
import { clinicianPayoutHelp } from "./clinicianPayoutHelp.js";
const PATIENT_HELP_CATEGORIES = [
  {
    slug: "getting-started",
    title: "Getting started",
    audience: "patient",
    summary: "Create your patient account, sign in safely, and understand the first choices AfriMed™ asks you to make.",
    icon: "Compass",
    status: "Ready",
    articles: [
      {
        slug: "patient-onboarding-overview",
        title: "Patient onboarding overview",
        summary: "Move from staging access into patient setup without mixing clinician account context.",
        steps: [
          "Choose Patient when AfriMed™ asks how you want to use the app.",
          "Complete the shared intro steps using your own name, phone number, passcode, date of birth, and account details.",
          "If OTP delivery is unavailable during staging, use Verify later only when shown. Deferred verification remains pending.",
          "Continue through patient profile setup, including ID/passport, language, blood type, conditions, allergies, and medications.",
          "Use Go to settings at completion so your patient profile can be reviewed before wider use.",
        ],
        callout: {
          tone: "trust",
          title: "Patient context",
          body: "Patient onboarding should save patient profile details only. It should not hydrate from a clinician session or local draft after completion.",
        },
        screenshots: [
          {
            src: "/assets/FAQ/onboarding/patient/mockups/log_in-01-02-03-magic-link.webp",
            label: "Sign in and magic link",
            caption: "Start from private staging access, choose email registration, and use the magic-link screen when signing in.",
            alt: "Three AfriMed mobile screens showing private staging access, email registration, and magic-link sign in.",
          },
          {
            src: "/assets/FAQ/onboarding/patient/mockups/onboarding-01-role-selection-step-name.webp",
            label: "Choose patient, then confirm your name",
            caption: "Select the patient role first, then enter the legal names that match your official ID.",
            alt: "AfriMed mobile screens showing patient role selection and the full names onboarding step.",
          },
        ],
      },
      {
        slug: "patient-sign-in-magic-link",
        title: "Sign in and magic link",
        summary: "Enter staging through the invite screen, register with email, and request a magic link when needed.",
        steps: [
          "Open the private staging access link from your invitation.",
          "Enter the invited email address and continue into AfriMed™ staging.",
          "Choose Register with email when creating your patient account.",
          "Use the Email sign-in option to request a magic link if you are returning without a password flow.",
          "Only open sign-in links that you requested yourself.",
        ],
        callout: {
          tone: "trust",
          title: "Invite-only access",
          body: "Staging access is private. Use the invited email address so the account stays attached to the right tester and patient context.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/log_in-01-02-03-magic-link.webp",
          label: "Staging access and email sign-in",
          caption: "Begin from private staging access, register by email, or request a magic link when returning.",
          alt: "Three AfriMed mobile screens showing private staging access, email registration, and magic-link sign in.",
        },
      },
      {
        slug: "patient-role-name",
        title: "Choose patient role and enter your name",
        summary: "Start in the patient pathway and enter names that match your official identification.",
        steps: [
          "Choose Patient when AfriMed™ asks how you want to use the app.",
          "Avoid continuing as Clinician if you are setting up a patient account.",
          "Enter your first name exactly as it appears on your official ID.",
          "Enter your family name exactly as it appears on your official ID.",
          "Continue only after the name fields are correct.",
        ],
        callout: {
          tone: "trust",
          title: "Patient role context",
          body: "The patient role keeps care, booking, records, and dependant information separate from clinician tools.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/onboarding-01-role-selection-step-name.webp",
          label: "Role selection and legal name",
          caption: "Select Patient first, then enter names that match your official identification.",
          alt: "AfriMed patient onboarding screens showing role selection and legal name entry.",
        },
      },
      {
        slug: "patient-phone-otp",
        title: "Verify phone or continue safely in staging",
        summary: "Add a phone number and understand what pending OTP verification means during staging.",
        steps: [
          "Enter the phone number you want attached to your patient account.",
          "Use the OTP field when SMS delivery is available.",
          "If staging offers Verify later, use it only when delivery is unavailable or the screen clearly allows it.",
          "Continue onboarding with the phone saved as pending, not verified.",
          "Return to phone verification when live OTP delivery is available.",
        ],
        callout: {
          tone: "warning",
          title: "Pending is not verified",
          body: "A deferred staging OTP helps you continue setup. It does not confirm phone ownership or enable SMS security by itself.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/patient-step-phone-step-otp.webp",
          label: "Phone number and OTP",
          caption: "Add your phone number, verify by OTP when available, or continue only through the staging-safe path shown.",
          alt: "AfriMed patient onboarding screens for phone number entry and OTP verification.",
        },
      },
      {
        slug: "patient-passcode",
        title: "Create and confirm passcode",
        summary: "Set the passcode used to protect quick access to your AfriMed™ account.",
        steps: [
          "Create a passcode you can remember without writing it in an unsafe place.",
          "Confirm the same passcode on the next screen.",
          "If the confirmation does not match, re-enter both passcode steps carefully.",
          "Avoid using a passcode that someone else can easily guess.",
        ],
        callout: {
          tone: "trust",
          title: "Account protection",
          body: "Your passcode protects access to AfriMed™ on shared or frequently handled devices.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/passcode-create-confirm.webp",
          label: "Passcode setup",
          caption: "Create and confirm the same passcode before moving into the next onboarding step.",
          alt: "AfriMed patient onboarding screens for creating and confirming a passcode.",
        },
      },
      {
        slug: "patient-dob-intro-complete",
        title: "Add date of birth and complete intro",
        summary: "Finish the shared intro details before patient-specific profile setup begins.",
        steps: [
          "Enter your date of birth using the format shown on the screen.",
          "Check the day, month, and year before continuing.",
          "Complete the intro screen once AfriMed™ confirms the shared setup section is done.",
          "Continue into patient-specific onboarding rather than stopping at the intro completion screen.",
        ],
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/StepDob-StepDone.webp",
          label: "Date of birth and intro completion",
          caption: "Add your date of birth, then continue once the shared intro section is complete.",
          alt: "AfriMed patient onboarding screens for date of birth and intro completion.",
        },
      },
      {
        slug: "patient-id-passport-consent",
        title: "ID / Passport consent and verification",
        summary: "Add identity details carefully and understand consent before document processing.",
        steps: [
          "Choose the ID or passport option shown for your account.",
          "Enter the identity details requested by the screen.",
          "Read the consent prompt before continuing.",
          "Only consent when you are comfortable with the identity-processing step shown.",
          "Treat any skipped or deferred identity review as pending until real verification is completed.",
        ],
        callout: {
          tone: "warning",
          title: "Consent is not verification",
          body: "Giving consent or entering document details does not by itself mean ID or passport verification has been completed.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/StepIDPassport-Consent.webp",
          label: "ID, passport, and consent",
          caption: "Complete identity fields truthfully and read consent language before continuing.",
          alt: "AfriMed patient onboarding screens for ID/passport details and consent.",
        },
      },
      {
        slug: "patient-id-ocr-language",
        title: "ID / Passport OCR and language",
        summary: "Use OCR only when shown and choose the language that helps you move through AfriMed™ confidently.",
        steps: [
          "Use the OCR or document capture step only when AfriMed™ shows it.",
          "Check any extracted details carefully before continuing.",
          "Do not treat OCR capture as completed verification unless AfriMed™ explicitly confirms verification.",
          "Choose the interface language you prefer for care and account setup.",
          "Return to language settings later if your preference changes.",
        ],
        callout: {
          tone: "warning",
          title: "OCR is processing support",
          body: "OCR can help read document details, but OCR capture alone does not prove identity verification is complete.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/StepIDPassport-OCR-StepLanguage.webp",
          label: "OCR and language",
          caption: "Use OCR only when prompted, then select the language that helps you use AfriMed™ confidently.",
          alt: "AfriMed patient onboarding screens for OCR and language selection.",
        },
      },
      {
        slug: "patient-blood-type-conditions",
        title: "Blood type and medical conditions",
        summary: "Add the basic health information AfriMed™ uses to support care context.",
        steps: [
          "Choose your blood type only if you know it accurately.",
          "Use an unknown or skip option if you are not sure.",
          "Add medical conditions that are relevant to care.",
          "Avoid guessing health information just to complete the screen.",
          "Review and update these details later in patient Settings when needed.",
        ],
        callout: {
          tone: "trust",
          title: "Accuracy matters",
          body: "Health profile fields should reflect what you know to be accurate. You can update them later as your records become clearer.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/StepBloodType-StepConditions.webp",
          label: "Blood type and conditions",
          caption: "Add known blood type and relevant conditions without guessing uncertain health information.",
          alt: "AfriMed patient onboarding screens for blood type and medical conditions.",
        },
      },
      {
        slug: "patient-allergies-medications",
        title: "Allergies and medications",
        summary: "Record allergies and medicines so care teams have safer context.",
        steps: [
          "Add known allergies that may affect care, medicines, or prescriptions.",
          "Use clear names where possible and avoid guessing uncertain allergy details.",
          "Add current medications you are taking.",
          "Review spelling, dose, and medication names if the screen asks for them.",
          "Update allergies and medications later if your care details change.",
        ],
        callout: {
          tone: "trust",
          title: "Keep care context current",
          body: "Allergies and medication details can affect safe care decisions, so keep them current and accurate.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/StepAllergies-StepMedications.webp",
          label: "Allergies and medications",
          caption: "Add known allergies and current medications so your patient profile has safer care context.",
          alt: "AfriMed patient onboarding screens for allergies and medications.",
        },
      },
      {
        slug: "patient-complete-open-settings",
        title: "Complete onboarding and open Settings",
        summary: "Finish patient onboarding, then review the saved patient profile in Settings.",
        steps: [
          "Complete the final onboarding screen after all required patient steps are done.",
          "Use Go to settings when AfriMed™ offers it.",
          "Check the Settings welcome screen for the correct patient context.",
          "Open patient Settings and review profile, account, health, and preference details.",
          "Refresh or sign in again later to confirm the profile still hydrates from saved patient data.",
        ],
        callout: {
          tone: "trust",
          title: "Review your saved profile",
          body: "Settings is where the patient profile can be reviewed before booking care, managing records, or using dependant workflows.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/patient/mockups/patient-step-complete-settings-welcome.webp",
          label: "Completion and Settings welcome",
          caption: "Finish onboarding, then move into the Settings welcome screen for the patient account.",
          alt: "AfriMed patient onboarding completion and Settings welcome screens.",
        },
      },
    ],
  },
  {
    slug: "profile",
    title: "Your profile",
    audience: "patient",
    summary: "Manage the details AfriMed™ uses for care, communication, dependants, and privacy-sensitive records.",
    icon: "UserRound",
    status: "Ready",
    articles: [
      {
        slug: "edit-intro",
        title: "Edit Intro",
        summary: "Update the main profile details people use to recognize you inside AfriMed™.",
        steps: [
          "Open Settings and select your profile row.",
          "Choose the profile or intro section available for your role.",
          "Update your display details and profile photo where supported.",
          "Save, then return to confirm the updated information is shown correctly.",
        ],
        screenshot: {
          label: "Screenshot slot",
          caption: "Use the patient settings/profile screenshot as the first visual reference.",
        },
      },
      {
        slug: "contact-data",
        title: "Contact data",
        summary: "Keep email, phone, and address details current for appointment and account communication.",
        steps: [
          "Open your profile settings.",
          "Review contact fields such as email, phone, location, and address.",
          "Update details that are out of date.",
          "Save changes before leaving the screen.",
        ],
        callout: {
          tone: "trust",
          title: "Communication accuracy",
          body: "Accurate contact details help AfriMed™ send appointment and account information to the right place.",
        },
        screenshot: {
          label: "Contact details screenshot slot",
          caption: "Add the profile contact screen after the settings walkthrough captures are approved.",
        },
      },
      {
        slug: "dependants",
        title: "Dependants",
        summary: "Add and manage family members whose care you coordinate through your account.",
        steps: [
          "Open your profile or dependant area.",
          "Add the dependant details requested by AfriMed™.",
          "Use the dependant selector during booking when the appointment is for that person.",
          "Review dependant information before confirming care-related actions.",
        ],
        callout: {
          tone: "info",
          title: "Currently expanding",
          body: "Dependant care is guardian-managed during staging. Separate dependant login and privacy handoff flows are future updates.",
        },
        screenshot: {
          label: "Dependants screenshot slot",
          caption: "Add the dependant management screen once the guardian walkthrough is captured.",
        },
      },
      {
        slug: "health-insurance",
        title: "Health insurance",
        summary: "Record insurance details for visibility during booking and profile review.",
        steps: [
          "Open the insurance area in your profile or booking flow.",
          "Select or enter the insurance information currently supported.",
          "Review how the payment method is shown before confirming an appointment.",
          "Update details when your cover or preferred payment method changes.",
        ],
        callout: {
          tone: "warning",
          title: "Available in future updates",
          body: "Insurance selection helps record how you intend to pay. Live insurance claims automation is not enabled yet.",
        },
        screenshot: {
          label: "Insurance screenshot slot",
          caption: "Add the insurance details screen when payment and cover walkthroughs are captured.",
        },
      },
      {
        slug: "medical-history",
        title: "Medical history",
        summary: "Use AfriMed™ to keep important health context readable and available for care.",
        steps: [
          "Open your profile or myEHR area.",
          "Review the available sections for conditions, allergies, visits, prescriptions, notes, and tests.",
          "Update fields that AfriMed™ allows you to maintain directly.",
          "Ask your clinician to review medical details that need clinical confirmation.",
        ],
        callout: {
          tone: "trust",
          title: "Clinical review matters",
          body: "Medical history helps care teams understand context, but clinical decisions remain clinician-reviewed.",
        },
        screenshot: {
          label: "myEHR screenshot slot",
          caption: "Add a readable health record screen once myEHR walkthrough images are ready.",
        },
      },
      {
        slug: "privacy-expectations",
        title: "Privacy expectations",
        summary: "Understand how AfriMed™ treats sensitive profile and care information.",
        steps: [
          "Use your own authenticated AfriMed™ account for private healthcare actions.",
          "Check account and security settings regularly.",
          "Avoid sharing screenshots that include personal health details.",
          "Use support channels when something looks incorrect or exposed in the wrong place.",
        ],
        callout: {
          tone: "trust",
          title: "Private by context",
          body: "Only authenticated users with the right care relationship should access private health information inside AfriMed™.",
        },
        screenshot: {
          label: "Privacy settings screenshot slot",
          caption: "Add the account and privacy settings screen when security walkthrough captures are available.",
        },
      },
    ],
  },
  {
    slug: "messages-fola",
    title: "Messages & Fola",
    audience: "patient",
    summary: "Use messages for care communication and understand how Fola supports without replacing clinicians.",
    icon: "MessagesSquare",
    status: "Ready",
    articles: [
      {
        slug: "what-fola-is",
        title: "What Fola is",
        summary: "Fola is AfriMed™'s support assistant for clearer, safer platform guidance.",
        steps: [
          "Use Fola for AfriMed™ workflow questions, message support, and plain-language explanations.",
          "Ask for help understanding next steps inside AfriMed™.",
          "Use supported language requests during staging when available.",
          "Review Fola output before acting on anything care-related.",
        ],
        callout: {
          tone: "trust",
          title: "Support-only positioning",
          body: "Fola can help explain AfriMed™ workflows and selected care information, but it does not diagnose, prescribe, or replace your clinician.",
        },
        screenshot: {
          label: "Screenshot slot",
          caption: "Add a message-thread screenshot showing Fola support language once approved.",
        },
      },
      {
        slug: "what-fola-is-not",
        title: "What Fola is not",
        summary: "Know the limits so healthcare trust stays clear.",
        steps: [
          "Do not use Fola as an emergency service.",
          "Do not treat Fola as a doctor or diagnostic tool.",
          "Do not use Fola output as a prescription or treatment plan.",
          "Contact a clinician or emergency service for urgent medical concerns.",
        ],
        callout: {
          tone: "critical",
          title: "Healthcare safety",
          body: "Fola is not an AI doctor. Clinical decisions remain with qualified clinicians.",
        },
      },
      {
        slug: "supported-staging-languages",
        title: "Supported staging languages",
        summary: "Understand how language requests work while AfriMed™ is still expanding language coverage.",
        steps: [
          "Ask Fola to respond in a supported AfriMed™ staging language when needed.",
          "Use clear language names such as Shona, isiNdebele, Zulu, or Portuguese.",
          "If a language is not supported, Fola may fall back to a safer supported response.",
          "Treat translations as support guidance, not clinical authority.",
        ],
        callout: {
          tone: "info",
          title: "Currently expanding",
          body: "Multilingual support is improving during staging and should not be presented as universal fluency.",
        },
        screenshot: {
          label: "Language support screenshot slot",
          caption: "Add a Fola language request screenshot after staging language captures are reviewed.",
        },
      },
      {
        slug: "messaging-a-clinician",
        title: "Messaging a clinician",
        summary: "Use AfriMed™ Messages to keep appointment and care communication in one place.",
        steps: [
          "Open Messages from AfriMed™ navigation.",
          "Select the clinician or conversation thread.",
          "Write a clear message with the context your clinician needs.",
          "Wait for clinician review before making care decisions.",
        ],
        callout: {
          tone: "trust",
          title: "Clinician-reviewed care",
          body: "Messages can support continuity, but urgent or emergency concerns should use the appropriate urgent care channel.",
        },
        screenshot: {
          label: "Messages screenshot slot",
          caption: "Add the AfriMed™ Messages thread screen once care communication captures are ready.",
        },
      },
      {
        slug: "thread-summaries",
        title: "Thread summaries",
        summary: "Use summaries to understand long conversations more easily.",
        steps: [
          "Open a conversation thread.",
          "Use the Fola summary action when it is available.",
          "Review the summary against the original messages.",
          "Ask your clinician if anything care-related is unclear.",
        ],
        screenshot: {
          label: "Thread summary screenshot slot",
          caption: "Add the thread summary screen when Fola summary captures are approved.",
        },
      },
      {
        slug: "draft-replies",
        title: "Draft replies",
        summary: "Use draft help to write clearer messages without sending automatically.",
        steps: [
          "Open the relevant thread.",
          "Ask Fola to help draft calm wording for your message.",
          "Edit the draft so it accurately reflects your situation.",
          "Send only when you are comfortable with the final wording.",
        ],
        callout: {
          tone: "trust",
          title: "You stay in control",
          body: "Fola can help draft language, but you review and send messages yourself.",
        },
        screenshot: {
          label: "Draft reply screenshot slot",
          caption: "Add a draft reply example once safe messaging screenshots are captured.",
        },
      },
      {
        slug: "medication-explanations",
        title: "Medication explanations",
        summary: "Ask for everyday-language explanations while keeping clinical authority with your clinician.",
        steps: [
          "Open the prescription or message context where explanation support is available.",
          "Ask Fola for a plain-language explanation.",
          "Check dose, timing, and warnings against the prescription label.",
          "Ask your clinician or pharmacist before changing how you take medicine.",
        ],
        callout: {
          tone: "critical",
          title: "Do not change medication from Fola alone",
          body: "Medication explanations are for understanding. Treatment changes must be clinician or pharmacist reviewed.",
        },
        screenshot: {
          label: "Medication explanation screenshot slot",
          caption: "Add a medication explanation screen only after clinical safety review.",
        },
      },
      {
        slug: "support-only-positioning",
        title: "Support-only positioning",
        summary: "Understand the promise AfriMed™ is making with Fola during staging.",
        steps: [
          "Use Fola for workflow guidance, summaries, drafts, and plain-language support.",
          "Treat Fola as a helper inside AfriMed™, not a replacement for care.",
          "Use clinicians, pharmacists, and emergency services for medical decisions.",
          "Report confusing or unsafe Fola output through support.",
        ],
        callout: {
          tone: "trust",
          title: "Honest beta language",
          body: "Fola features are still being refined during staging. AfriMed™ will keep the experience clear about what is live and what is still expanding.",
        },
      },
    ],
  },
  {
    slug: "appointments",
    title: "Appointments",
    audience: "patient",
    summary: "Booking, reviewing, changing, and preparing for appointments.",
    icon: "CalendarDays",
    status: "Currently expanding",
    articles: [
      {
        slug: "booking-an-appointment",
        title: "Booking an appointment",
        summary: "Choose a care option, review availability, and confirm the appointment details.",
        steps: [
          "Open Appointments and choose the booking path that matches the care you need.",
          "Search or browse available clinicians, services, locations, or video visit options.",
          "Review the date, time, clinician, fee, language, and visit type before continuing.",
          "Confirm the appointment only when the details match the person receiving care.",
        ],
        callout: {
          tone: "info",
          title: "Currently expanding",
          body: "Appointment availability can vary by clinician, market, and staging account. If a slot is unavailable, check again later or use support guidance.",
        },
        screenshot: {
          label: "Appointment booking screenshot slot",
          caption: "Add the appointment booking screen once the walkthrough capture set is ready.",
        },
      },
      {
        slug: "changing-or-cancelling",
        title: "Changing or cancelling an appointment",
        summary: "Know where to review appointment details before making a change.",
        steps: [
          "Open Appointments and choose the upcoming appointment you want to review.",
          "Check the visit type, clinician, location, and payment details before changing anything.",
          "Use the available change or cancel action if it is enabled for your account.",
          "If the action is not available, contact support or follow the clinic instructions shown in the appointment.",
        ],
        callout: {
          tone: "warning",
          title: "Staging limitation",
          body: "Some change and cancellation controls are still being enabled. AfriMed™ should not imply a change is confirmed until the appointment status updates.",
        },
        screenshot: {
          label: "Appointment detail screenshot slot",
          caption: "Add the upcoming appointment detail screen when booking management captures are available.",
        },
      },
      {
        slug: "preparing-for-a-visit",
        title: "Preparing for a visit",
        summary: "Check practical details before an in-person or remote appointment.",
        steps: [
          "Open the appointment detail page before the visit.",
          "Review the appointment time, location or video link, clinician, and reason for visit.",
          "Prepare any questions, medication names, or documents you want the clinician to review.",
          "Use emergency services or direct clinician contact for urgent symptoms instead of waiting for a scheduled visit.",
        ],
        callout: {
          tone: "trust",
          title: "Care readiness",
          body: "Good preparation helps the visit run smoothly, but urgent clinical concerns should use urgent care channels.",
        },
        screenshot: {
          label: "Visit preparation screenshot slot",
          caption: "Add a visit detail or preparation screen once appointment screenshots are captured.",
        },
      },
    ],
  },
  {
    slug: "prescriptions-myehr",
    title: "Prescriptions & myEHR",
    audience: "patient",
    summary: "Prescription records, verification, visit history, notes, and readable health context.",
    icon: "ClipboardList",
    status: "Currently expanding",
    articles: [
      {
        slug: "viewing-prescriptions",
        title: "Viewing prescriptions",
        summary: "Find prescription records and understand what details are available.",
        steps: [
          "Open Prescriptions or myEHR from the area available to your account.",
          "Select the prescription record you want to review.",
          "Check medicine name, dose, timing, clinician, date, and status where shown.",
          "Ask your clinician or pharmacist before changing how you take medicine.",
        ],
        callout: {
          tone: "critical",
          title: "Medication safety",
          body: "Prescription records support understanding. Treatment changes must be clinician or pharmacist reviewed.",
        },
        screenshot: {
          label: "Prescription screenshot slot",
          caption: "Add the prescription record screen after clinical safety review.",
        },
      },
      {
        slug: "understanding-myehr",
        questions: clinicalOutputsHelp.en.questions.slice(0,2),
        title: "Understanding myEHR",
        summary: "Use myEHR as readable health context for visits, prescriptions, and care history.",
        steps: [
          "Open myEHR from your profile, records, or prescriptions area.",
          "Review sections such as visits, notes, prescriptions, allergies, and medical history where available.",
          "Use the information to prepare for appointments or understand previous care.",
          "Ask a clinician to review anything that appears incorrect or clinically important.",
        ],
        callout: {
          tone: "trust",
          title: "Readable health context",
          body: "myEHR helps organize information, but AfriMed™ does not turn records into self-diagnosis or automatic treatment advice.",
        },
        screenshot: {
          label: "myEHR overview screenshot slot",
          caption: "Add the myEHR overview screen once health record captures are ready.",
        },
      },
      {
        slug: "downloading-records",
        title: "Downloading records",
        summary: "Prepare prescription or health record information for personal review where downloads are enabled.",
        steps: [
          "Open the prescription, invoice, note, or record you want to keep.",
          "Use the download action if it is visible and enabled for your account.",
          "Store the downloaded file somewhere private and secure.",
          "Share records only with people or clinicians you trust.",
        ],
        callout: {
          tone: "info",
          title: "Available in future updates",
          body: "Some record downloads are still being expanded. If no download action appears, the record may be view-only during staging.",
        },
        screenshot: {
          label: "Record download screenshot slot",
          caption: "Add the download action screen when export walkthroughs are captured.",
        },
      },
    ],
  },
  {
    slug: "payments-insurance",
    title: "Payments & insurance",
    audience: "patient",
    summary: "Secure payment methods, confirmation, recovery, reversals, refunds, receipts, and insurance visibility.",
    icon: "CreditCard",
    status: "Ready",
    articles: [
      {
        slug: "choosing-payment-method",
        title: "Choosing a payment method",
        summary: "Choose an available AfriMed™ payment method and understand when the booking is created.",
        steps: [
          "At Secure Checkout, review the amount, currency, patient, clinician, service, and appointment time.",
          "Choose an available method: Credit • Debit Card, Instant EFT, Scan to Pay, MTN MoMo, or Sponsor payment.",
          "Complete the secure provider step shown for the selected method, then return to AfriMed™ if prompted.",
          "Wait for AfriMed™ to verify the payment. A booking is not created before verified payment, which prevents an unpaid or duplicate appointment.",
          "After verification, AfriMed™ creates the booking and shows the normal booking confirmation.",
        ],
        callout: {
          tone: "trust",
          title: "Verified payment protects the booking",
          body: "Available methods can differ by market. AfriMed™ shows the supported options for the booking and creates the appointment only after it receives verified payment confirmation.",
        },
        screenshot: {
          label: "Payment method screenshot slot",
          caption: "Add the payment method screen once billing walkthrough captures are available.",
        },
      },
      {
        slug: "receipts-and-invoices",
        title: "Receipts and invoices",
        summary: "Open, print, and download appointment and refund evidence from Transactions & invoices.",
        steps: [
          "Open Transactions & invoices from Settings and select the relevant payment or refund event.",
          "Use Appointment receipt for the original verified payment and Refund receipt for the reversal outcome.",
          "Check the patient, clinician, appointment, consultation or location, payment method, amount, status, and reference.",
          "Use Print to open the print-ready receipt, or Download receipt to save the PDF.",
          "A refunded payment remains visible alongside the original Paid event so the financial history stays complete.",
        ],
        callout: {
          tone: "trust",
          title: "Refund destination",
          body: "Refunded to the original payment method. Processing time after AfriMed™ confirms the refund can depend on the original payment method and financial institution.",
        },
        screenshot: {
          label: "Invoice screenshot slot",
          caption: "Add the invoice or receipt screen when billing captures are ready.",
        },
      },
      {
        slug: "payment-status-recovery-refunds",
        title: "Payment status, recovery, cancellation, and refunds",
        summary: "Understand pending, failed, declined, expired, reversed, and refunded payment states.",
        steps: [
          "If payment is pending, keep the selected booking details and use Check payment status or pending-payment recovery instead of starting another payment.",
          "A failed, declined, rejected, or expired payment does not create a booking; choose an available method and try again when ready.",
          "If verified payment completed but confirmation was interrupted, AfriMed™ recovers the payment status before creating the booking, avoiding a duplicate booking request.",
          "When a paid appointment is cancelled, AfriMed™ records the cancellation and starts or records the required payment reversal.",
          "When the reversal is verified, the refund is returned to the original payment method and appears in Transactions & invoices with a Refund receipt.",
          "Use Notifications and Transactions & invoices to follow payment confirmation, reversal, and refund status.",
        ],
        callout: {
          tone: "warning",
          title: "Do not pay twice",
          body: "If a payment appears pending or the confirmation page was interrupted, recover or check that payment first. Start another payment only after AfriMed™ confirms that the earlier attempt failed or expired.",
        },
      },
      {
        slug: "insurance-selection",
        title: "Insurance selection",
        summary: "Record insurance visibility without assuming live claims automation.",
        steps: [
          "Open the insurance area in your profile or booking flow.",
          "Select the insurance option currently supported for your account.",
          "Review the selected cover before confirming an appointment.",
          "Use support if the insurer shown does not match your records.",
        ],
        callout: {
          tone: "warning",
          title: "Available in future updates",
          body: "Insurance selection is for visibility during staging. Automated claims submission and approval are still expanding.",
        },
        screenshot: {
          label: "Insurance selection screenshot slot",
          caption: "Add the insurance selection screen once cover workflows are captured.",
        },
      },
      {
        slug: "sponsor-payment-links",
        title: "Sponsor payment links",
        summary: "Use a governed AfriMed™ sponsor payment link for family or diaspora support.",
        steps: [
          "Choose Sponsor payment at Secure Checkout or prepare a sponsor payment from Transactions & invoices.",
          "Check the patient, amount, currency, appointment, and reason before sharing the secure AfriMed™ payment link.",
          "The sponsor completes the enabled payment route without receiving access to the Patient's clinical record.",
          "Return to AfriMed™ and confirm the verified payment status; the booking is created only after that verification.",
          "If the sponsor return is interrupted, use pending-payment recovery instead of creating a second payment.",
        ],
        callout: {
          tone: "trust",
          title: "Sponsor privacy",
          body: "The sponsor pays through an AfriMed™ payment link. Payment support does not grant access to appointments, messages, health records, or other clinical information.",
        },
        screenshot: {
          label: "Sponsor payment screenshot slot",
          caption: "Add the sponsor payment screen when link generation captures are approved.",
        },
      },
    ],
  },
  {
    slug: "dependants-family-care",
    title: "Dependants & family care",
    audience: "patient",
    summary: "Guardian-managed care, dependant appointments, and future privacy handoff work.",
    icon: "UsersRound",
    status: "Currently expanding",
    articles: [
      {
        slug: "adding-a-dependant",
        title: "Adding a dependant",
        summary: "Create a dependant profile for someone whose care you coordinate.",
        steps: [
          "Open your profile or dependants area.",
          "Choose the option to add a dependant when it is available.",
          "Enter the dependant details requested by AfriMed™.",
          "Review the information carefully before using it for appointments or records.",
        ],
        callout: {
          tone: "info",
          title: "Currently expanding",
          body: "Dependant profiles are guardian-managed during staging. Separate dependant account handoff and consent flows are future updates.",
        },
        screenshot: {
          label: "Add dependant screenshot slot",
          caption: "Add the dependant creation screen once guardian workflow captures are ready.",
        },
      },
      {
        slug: "booking-for-a-dependant",
        title: "Booking for a dependant",
        summary: "Make sure the appointment is attached to the correct family member.",
        steps: [
          "Start an appointment booking from the appointment area.",
          "Select the dependant who will receive care when the selector is available.",
          "Review clinician, date, time, visit type, and payment details for that dependant.",
          "Confirm only after checking that the appointment is not attached to the wrong profile.",
        ],
        callout: {
          tone: "trust",
          title: "Family care context",
          body: "Dependant booking helps coordinate care, but each appointment should clearly show who the visit is for.",
        },
        screenshot: {
          label: "Dependant booking screenshot slot",
          caption: "Add the dependant selector or dependant appointment screen once captured.",
        },
      },
      {
        slug: "dependant-privacy",
        title: "Dependant privacy",
        summary: "Understand what is still being refined for family care privacy.",
        steps: [
          "Use only your own AfriMed™ account to manage dependant care.",
          "Review dependant details before sharing or confirming care actions.",
          "Avoid sending screenshots that expose a dependant's health information.",
          "Use support if dependant information appears in the wrong account or context.",
        ],
        callout: {
          tone: "warning",
          title: "Available in future updates",
          body: "Advanced dependant privacy handoff, age-based transitions, and separate dependant access controls are still being expanded.",
        },
        screenshot: {
          label: "Dependant privacy screenshot slot",
          caption: "Add privacy and dependant access screens after the handoff model is finalized.",
        },
      },
    ],
  },
  {
    slug: "video-consultations",
    title: "Video consultations",
    audience: "patient",
    summary: "Joining video visits, checking access, and preparing for remote care.",
    icon: "Video",
    status: "Currently expanding",
    articles: [
      {
        slug: "joining-a-video-consultation",
        title: "Joining a video consultation",
        summary: "Use the appointment details to join a remote visit when video is enabled.",
        steps: [
          "Open the upcoming appointment before the scheduled time.",
          "Check whether the appointment is marked as a video consultation.",
          "Use the join action when it becomes available.",
          "Keep your device charged and stay somewhere private for the consultation.",
        ],
        callout: {
          tone: "info",
          title: "Currently expanding",
          body: "Video join controls may only appear for supported appointment types and enabled staging accounts.",
        },
        screenshot: {
          label: "Video consultation screenshot slot",
          caption: "Add the video appointment screen when remote visit captures are ready.",
        },
      },
      {
        slug: "checking-video-access",
        title: "Checking video access",
        summary: "Prepare your browser, camera, microphone, and connection before a remote visit.",
        steps: [
          "Open AfriMed™ on a device with a stable internet connection.",
          "Allow camera and microphone permissions when prompted by your browser.",
          "Check that headphones, camera, and microphone work before the visit.",
          "Use support or the appointment instructions if the video action does not appear.",
        ],
        callout: {
          tone: "trust",
          title: "Private remote care",
          body: "Choose a private location for video visits so health details are not exposed to people nearby.",
        },
        screenshot: {
          label: "Video access screenshot slot",
          caption: "Add browser permission and video readiness screens after capture.",
        },
      },
    ],
  },
  {
    slug: "privacy-security",
    title: "Privacy & security",
    audience: "patient",
    summary: "Account security, password safety, healthcare privacy, and authenticated access.",
    icon: "ShieldCheck",
    status: "Currently expanding",
    articles: [
      {
        slug: "keeping-your-account-secure",
        title: "Keeping your account secure",
        summary: "Protect the AfriMed™ account that holds appointments, messages, and health records.",
        steps: [
          "Use a strong password that you do not reuse on other services.",
          "Keep your email and phone details current so recovery messages reach you.",
          "Sign out on shared devices after using AfriMed™.",
          "Contact support if you notice access you do not recognize.",
        ],
        callout: {
          tone: "trust",
          title: "Private healthcare access",
          body: "AfriMed™ account security protects care messages, appointment details, and health information.",
        },
        screenshot: {
          label: "Security settings screenshot slot",
          caption: "Add account security screens once the settings walkthrough is captured.",
        },
      },
      {
        slug: "health-information-privacy",
        title: "Health information privacy",
        summary: "Understand the privacy expectations around profile, message, and record data.",
        steps: [
          "Use authenticated AfriMed™ screens for healthcare actions.",
          "Avoid sharing screenshots that show health records, prescriptions, or private messages.",
          "Review the account context before opening dependant or clinician-linked information.",
          "Use support if private information appears in the wrong place.",
        ],
        callout: {
          tone: "trust",
          title: "Context matters",
          body: "AfriMed™ is designed to keep healthcare information attached to the right account, role, and care relationship.",
        },
        screenshot: {
          label: "Privacy guidance screenshot slot",
          caption: "Add privacy and account context screens after approved captures are ready.",
        },
      },
    ],
  },
  {
    slug: "technical-support",
    title: "Technical support",
    audience: "patient",
    summary: "Troubleshooting access, devices, notifications, and support requests.",
    icon: "LifeBuoy",
    status: "Currently expanding",
    articles: [
      {
        slug: "troubleshooting-access",
        title: "Troubleshooting access",
        summary: "Work through the most common reasons AfriMed™ may not open or sign in correctly.",
        steps: [
          "Check that your internet connection is working.",
          "Refresh the page or reopen AfriMed™.",
          "Confirm you are using the correct email address for your account.",
          "Use Help & support if access still does not work after trying the basics.",
        ],
        callout: {
          tone: "info",
          title: "Support access",
          body: "Use Help & support to create a request and review its status. If access cannot be confirmed, retry or use the account-access guidance.",
        },
        screenshot: {
          label: "Access support screenshot slot",
          caption: "Add sign-in or access error screens once troubleshooting captures are ready.",
        },
      },
      {
        slug: "browser-device-notifications",
        title: "Browser, device, and notifications",
        summary: "Check the basics when a page, upload, camera, or notification is not behaving as expected.",
        steps: [
          "Use a current browser version on a device with enough battery and network signal.",
          "Allow permissions for camera, microphone, files, or notifications only when the AfriMed™ screen asks for them.",
          "Try refreshing the page if a screen looks incomplete.",
          "Send support the screen name and what you expected to happen if the issue continues.",
        ],
        callout: {
          tone: "trust",
          title: "Share safely",
          body: "When reporting technical issues, avoid sending screenshots that expose private health details unless support specifically asks for them through an approved channel.",
        },
        screenshot: {
          label: "Technical support screenshot slot",
          caption: "Add browser permission or device troubleshooting screens once captured.",
        },
      },
    ],
  },
];

const DOCTOR_HELP_CATEGORIES = [
  {slug:"clinical-outputs",title:"Clinical outputs and secure exchange",audience:"doctor",summary:clinicalOutputsHelp.en.summary,icon:"FileText",status:"Ready",articles:[{slug:"interoperability",...clinicalOutputsHelp.en}]},
  {
    slug: "clinician-settlement", title: "Consultation payouts", audience: "doctor", summary: clinicianPayoutHelp.en.summary, icon: "CreditCard", status: "Ready",
    articles: [{ slug: "clinician-payout-account", ...clinicianPayoutHelp.en }],
  },
  {
    slug: "doctor-getting-started",
    title: "Getting started as a doctor",
    audience: "doctor",
    summary: "Set up a clinician account, complete staging-safe verification steps, and reach doctor Settings with the right profile truth.",
    icon: "Stethoscope",
    status: "Ready",
    articles: [
      {
        slug: "doctor-onboarding-overview",
        title: "Doctor onboarding overview",
        summary: "Move from role selection into clinician setup without patient account residue.",
        steps: [
          "Choose Doctor or Clinician when AfriMed™ asks how you want to use the app.",
          "Complete the shared intro steps using your own name, phone number, pass code, date of birth, and account details.",
          "If OTP delivery is unavailable during staging, use Verify later only when shown. Deferred verification remains pending.",
          "Continue through doctor setup, including ID/passport, languages, specialty, registration, formation, registration details, and About.",
          "Use Go to settings at completion so your clinician profile can be reviewed before operational use.",
        ],
        callout: {
          tone: "trust",
          title: "Clinician context",
          body: "Doctor onboarding should save canonical doctor profile details. It should not hydrate from an old patient session or local draft after completion.",
        },
        screenshots: [
          {
            src: "/assets/FAQ/onboarding/clinician/mockups/log_in-01-02-03-magic-link.webp",
            label: "Sign in and magic link",
            caption: "Start from private staging access, choose email registration, and use the magic-link screen when signing in.",
            alt: "Three AfriMed mobile screens showing private staging access, email registration, and magic-link sign in.",
          },
          {
            src: "/assets/FAQ/onboarding/clinician/mockups/clinician-role-selection-step-name.webp",
            label: "Choose clinician, then confirm your name",
            caption: "Select the clinician role first, then enter the legal names that match your official ID.",
            alt: "Two AfriMed mobile screens showing clinician role selection and the full names onboarding step.",
          },
        ],
      },
      {
        slug: "doctor-sign-in-magic-link",
        title: "Sign in and magic link",
        summary: "Enter staging through the invite screen, register with email, and request a magic link when needed.",
        steps: [
          "Open the private staging access link from your invitation.",
          "Enter the invited email address and continue into AfriMed™ staging.",
          "Choose Register with email when creating your clinician account.",
          "Use the Email sign-in option to request a magic link if you are returning without a password flow.",
          "Only open sign-in links that you requested yourself.",
        ],
        callout: {
          tone: "trust",
          title: "Invite-only access",
          body: "Staging access is private. Use the invited email address so the account stays attached to the right tester and clinician context.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/log_in-01-02-03-magic-link.webp",
          label: "Staging access and email sign-in",
          caption: "Begin from private staging access, register by email, or request a magic link when returning.",
          alt: "Three AfriMed mobile screens showing private staging access, email registration, and magic-link sign in.",
        },
      },
      {
        slug: "doctor-role-name",
        title: "Choose clinician role and enter your name",
        summary: "Start in the clinician pathway and enter names that match your official identification.",
        steps: [
          "Choose Clinician when AfriMed™ asks how you want to use the app.",
          "Avoid continuing as Patient if you are setting up a doctor account.",
          "Enter your first name exactly as it appears on your official ID.",
          "Enter your family name exactly as it appears on your official ID.",
          "Continue only after the name fields are correct.",
        ],
        callout: {
          tone: "trust",
          title: "Clinician role context",
          body: "The clinician role keeps doctor onboarding, settings, and profile data separate from patient account information.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/clinician-role-selection-step-name.webp",
          label: "Role selection and legal name",
          caption: "Select Clinician first, then enter names that match your official identification.",
          alt: "Two AfriMed mobile screens showing clinician role selection and the full names onboarding step.",
        },
      },
      {
        slug: "doctor-phone-otp",
        title: "Verify phone or continue safely in staging",
        summary: "Add a phone number and understand what pending OTP verification means during staging.",
        steps: [
          "Enter the phone number you want attached to your clinician account.",
          "Use the OTP field when SMS delivery is available.",
          "If staging offers Verify later, use it only when delivery is unavailable or the screen clearly allows it.",
          "Continue onboarding with the phone saved as pending, not verified.",
          "Return to phone verification when live OTP delivery is available.",
        ],
        callout: {
          tone: "warning",
          title: "Pending is not verified",
          body: "A deferred staging OTP helps you continue setup. It does not confirm phone ownership or enable SMS security by itself.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-phone-step-otp.webp",
          label: "Phone number and OTP",
          caption: "Add your phone number, verify by OTP when available, or continue only through the staging-safe path shown.",
          alt: "AfriMed clinician onboarding screens for phone number entry and OTP verification.",
        },
      },
      {
        slug: "doctor-passcode",
        title: "Create and confirm passcode",
        summary: "Set the passcode used to protect quick access to your AfriMed™ account.",
        steps: [
          "Create a passcode you can remember without writing it in an unsafe place.",
          "Confirm the same passcode on the next screen.",
          "If the confirmation does not match, re-enter both passcode steps carefully.",
          "Avoid using a passcode that colleagues, staff, or family can guess.",
        ],
        callout: {
          tone: "trust",
          title: "Account protection",
          body: "Your passcode protects access to AfriMed™ on shared or frequently handled devices.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-passcode-create-confirm.webp",
          label: "Passcode setup",
          caption: "Create and confirm the same passcode before moving into the next onboarding step.",
          alt: "AfriMed clinician onboarding screens for creating and confirming a passcode.",
        },
      },
      {
        slug: "doctor-dob-intro-complete",
        title: "Add date of birth and complete intro",
        summary: "Finish the shared intro details before clinician-specific setup begins.",
        steps: [
          "Enter your date of birth using the format shown on the screen.",
          "Check the day, month, and year before continuing.",
          "Complete the intro screen once AfriMed™ confirms the shared setup section is done.",
          "Continue into clinician-specific onboarding rather than stopping at the intro completion screen.",
        ],
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-dob-step-done.webp",
          label: "Date of birth and intro completion",
          caption: "Add your date of birth, then continue once the shared intro section is complete.",
          alt: "AfriMed clinician onboarding screens for date of birth and intro completion.",
        },
      },
      {
        slug: "doctor-id-passport-staging-bypass",
        title: "ID / Passport verification and staging bypass",
        summary: "Add identity details without treating staging bypass as completed verification.",
        steps: [
          "Choose the ID or passport option shown for your account.",
          "Enter the identity details requested by the screen.",
          "Read any consent or OCR prompt before continuing.",
          "Use a staging bypass only when the screen explicitly offers it.",
          "Treat bypassed identity review as pending until real verification is completed.",
        ],
        callout: {
          tone: "warning",
          title: "No fake verification",
          body: "A staging bypass must not imply government, ID, passport, or document verification unless that verification actually happened.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-id-passport-bypass-consent-ocr.webp",
          label: "ID, passport, consent, and OCR",
          caption: "Complete the identity screens truthfully; staging bypass means pending review, not verified identity.",
          alt: "AfriMed clinician onboarding screens for ID/passport, staging bypass, consent, and OCR.",
        },
      },
      {
        slug: "doctor-languages-specialty",
        title: "Languages, specialty, and subspecialties",
        summary: "Tell AfriMed™ which languages you use and what clinical area best represents your practice.",
        steps: [
          "Choose the languages you can comfortably use for care communication.",
          "Select your primary specialty from the supported list.",
          "Add subspecialty information when the next screen asks for it.",
          "Use the closest accurate match if the exact label is still being expanded during staging.",
          "Review these fields later in doctor Settings when needed.",
        ],
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-language-step-specialty.webp",
          label: "Languages and specialty",
          caption: "Select care languages and your primary specialty before continuing into subspecialty setup.",
          alt: "AfriMed clinician onboarding screens for selecting languages and specialty.",
        },
      },
      {
        slug: "doctor-subspecialty-registration-upload",
        title: "Registration upload and professional details",
        summary: "Add subspecialty context and upload professional registration evidence for later review.",
        steps: [
          "Complete the subspecialty screen with accurate professional context.",
          "Upload the registration document or image requested by the screen.",
          "Use a clear file that matches your professional registration details.",
          "Remember that upload is not the same as completed verification.",
          "Continue once the file is attached or the staging-safe path is shown.",
        ],
        callout: {
          tone: "warning",
          title: "Upload is review input",
          body: "A registration upload gives AfriMed™ material to review. It does not by itself mark professional registration as verified.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-subspecialty-step-regupload.webp",
          label: "Subspecialty and registration upload",
          caption: "Add subspecialty details and upload registration evidence for later verification review.",
          alt: "AfriMed clinician onboarding screens for subspecialty selection and registration upload.",
        },
      },
      {
        slug: "doctor-formation-registration-details",
        title: "Formation and registration details",
        summary: "Record training and professional registration information accurately.",
        steps: [
          "Add formation or training details requested by the onboarding screen.",
          "Enter registration details exactly as they appear in your professional records.",
          "Check spelling, registration numbers, and dates before continuing.",
          "Leave unsupported claims out of the profile until AfriMed™ supports verified review for them.",
        ],
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-formation-step-regdetails.webp",
          label: "Formation and registration details",
          caption: "Enter training and registration information carefully so later review has the right source details.",
          alt: "AfriMed clinician onboarding screens for formation and registration details.",
        },
      },
      {
        slug: "doctor-about-fola-bio",
        title: "About you and Fola bio draft",
        summary: "Write a patient-facing clinician bio and use Fola support carefully when drafting.",
        steps: [
          "Add a short About section that describes your clinical practice clearly.",
          "Keep the tone professional, understandable, and patient-facing.",
          "Use Fola drafting support only as a writing aid.",
          "Review and edit any generated text before saving it to your clinician profile.",
          "Avoid unsupported claims, exaggerated credentials, or unverified specialties.",
        ],
        callout: {
          tone: "trust",
          title: "You approve the bio",
          body: "Fola can help draft wording, but the final About text should be reviewed and approved by the clinician.",
        },
        screenshot: {
          src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-about-01-02-03.webp",
          label: "About section and Fola draft",
          caption: "Draft a clear clinician bio, review the wording, and keep patient-facing claims accurate.",
          alt: "AfriMed clinician onboarding screens for About text and Fola-supported bio drafting.",
        },
      },
      {
        slug: "doctor-complete-open-settings",
        title: "Complete onboarding and open Settings",
        summary: "Finish clinician onboarding, then review the saved doctor profile in Settings.",
        steps: [
          "Complete the final onboarding screen after all required clinician steps are done.",
          "Use Go to settings when AfriMed™ offers it.",
          "Check the Settings welcome screen for the correct doctor context.",
          "Open doctor Settings and review profile, verification, and practice details.",
          "Refresh or sign in again later to confirm the profile still hydrates from saved doctor data.",
        ],
        callout: {
          tone: "trust",
          title: "Review before operational use",
          body: "Settings is where the clinician profile can be reviewed before wider patient-facing or operational use.",
        },
        screenshots: [
          {
            src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-complete-settings-welcome.webp",
            label: "Completion and Settings welcome",
            caption: "Finish onboarding, then move into the Settings welcome screen for the clinician account.",
            alt: "AfriMed clinician onboarding completion and Settings welcome screens.",
          },
          {
            src: "/assets/FAQ/onboarding/clinician/mockups/clinician-step-settings-home.webp",
            label: "Doctor Settings home",
            caption: "Review the doctor Settings home so profile, verification, and practice details can be checked.",
            alt: "AfriMed doctor Settings home screen after onboarding.",
          },
        ],
      },
    ],
  },
  {
    slug: "doctor-profile-setup",
    title: "Doctor profile setup",
    audience: "doctor",
    summary: "Maintain the clinician profile details patients and care teams use to recognize your practice.",
    icon: "UserRound",
    status: "Ready",
    articles: [
      {
        slug: "clinician-intro-profile",
        title: "Clinician intro and profile",
        summary: "Review your visible doctor identity after onboarding.",
        steps: [
          "Open Doctor Settings after onboarding completion.",
          "Review name, professional display details, profile photo, and About information.",
          "Update patient-facing details only from the doctor profile area.",
          "Refresh or sign in again to confirm the profile hydrates from saved doctor data.",
        ],
        screenshot: {
          label: "Doctor settings screenshot slot",
          caption: "Add doctor settings and profile screenshots after the staging doctor run.",
        },
      },
      {
        slug: "specialty-subspecialty",
        title: "Specialty and subspecialty",
        summary: "Keep clinical specialty information accurate for discovery and appointment context.",
        steps: [
          "Open the specialty area during onboarding or doctor Settings.",
          "Select your primary specialty and any supported subspecialty fields.",
          "Save changes and check that patient-facing discovery uses the same values.",
          "Use support if specialty information disappears after refresh.",
        ],
      },
      {
        slug: "languages-and-care-context",
        title: "Languages and care context",
        summary: "Show which languages you can support in consultations.",
        steps: [
          "Open doctor profile or onboarding language selection.",
          "Choose the languages you can confidently use with patients.",
          "Save the profile and review the visible doctor summary.",
          "Update languages when your practice support changes.",
        ],
      },
    ],
  },
  {
    slug: "doctor-verification-credentials",
    title: "Verification and credentials",
    audience: "doctor",
    summary: "Track ID/passport, registration, training, and credential status without overstating verification.",
    icon: "ShieldCheck",
    status: "Ready",
    articles: [
      {
        slug: "identity-and-registration",
        title: "Identity and registration",
        summary: "Save clinician identity details for later verification review.",
        steps: [
          "Enter ID/passport details only in the doctor verification step.",
          "Add registration or license fields where AfriMed™ asks for them.",
          "Use staging bypass only as pending verification.",
          "Check Settings after completion to confirm the saved values remain attached to the doctor profile.",
        ],
        callout: {
          tone: "warning",
          title: "Pending means pending",
          body: "Saved doctor ID, passport, and registration details are not government or council verified until AfriMed™ performs that verification.",
        },
      },
      {
        slug: "formation-and-credentials",
        title: "Formation and credentials",
        summary: "Record clinician training and credential details for review.",
        steps: [
          "Open the formation or credentials step during doctor onboarding.",
          "Enter qualifications and registration details requested by AfriMed™.",
          "Upload documents only where a secure upload action is shown.",
          "Confirm the status remains pending when review has not happened yet.",
        ],
      },
      {
        slug: "credential-upload-protection",
        title: "How credential uploads are protected",
        summary: "Understand how uploaded registration documents are handled during verification.",
        steps: [
          "AfriMed™ uses Supabase infrastructure with TLS encryption in transit and AES-256 encryption at rest.",
          "Supabase maintains SOC 2 Type 2 and ISO 27001 controls for its managed infrastructure.",
          "Uploaded registration documents are used for verification review and remain pending until AfriMed™ completes that review.",
          "A pending or uploaded credential does not mean the clinician is verified, approved for discovery, or cleared for regulated clinical activity.",
        ],
        callout: {
          tone: "info",
          title: "Security language matters",
          body: "AfriMed™ should not be described as HIPAA certified, formally audited, or impossible to hack unless those legal and technical audits have been completed.",
        },
      },
    ],
  },
  {
    slug: "doctor-availability-consultancies",
    title: "Availability and consultancies",
    audience: "doctor",
    summary: "Prepare practice locations, availability, services, and consultancies for booking.",
    icon: "CalendarDays",
    status: "Currently expanding",
    articles: [
      {
        slug: "practice-facility-setup",
        title: "Practice and facility setup",
        summary: "Attach the right clinic or practice context to doctor services.",
        steps: [
          "Open doctor Settings and find practice or facility setup.",
          "Review facility name, address, contact details, and consultation context.",
          "Save only facilities you are authorized to use.",
          "Check that booking or profile screens show the intended practice information.",
        ],
      },
      {
        slug: "services-and-consultancies",
        title: "Services and consultancies",
        summary: "Configure consultation types patients can request or book.",
        steps: [
          "Open services or consultancies in doctor Settings.",
          "Add supported consultation types, duration, mode, and price where enabled.",
          "Review how each service appears before making it bookable.",
          "Use staging copy as pending if payments or booking controls are still being expanded.",
        ],
      },
      {
        slug: "availability-basics",
        title: "Availability basics",
        summary: "Keep appointment availability aligned with when you can actually consult.",
        steps: [
          "Open the availability area in doctor Settings.",
          "Review working hours, blocked times, and consultation modes.",
          "Save changes and check appointment screens for the expected slots.",
          "Do not treat unavailable or hidden slots as confirmed bookings.",
        ],
      },
    ],
  },
  {
    slug: "doctor-appointments",
    title: "Appointments",
    audience: "doctor",
    summary: "Review appointment requests, prepare for visits, and manage consultation state honestly.",
    icon: "CalendarCheck",
    status: "Currently expanding",
    articles: [
      {
        slug: "reviewing-appointments",
        title: "Reviewing appointments",
        summary: "Use the doctor dashboard to understand upcoming care work.",
        steps: [
          "Open the doctor home or appointments area.",
          "Review patient name, appointment time, visit type, service, and status.",
          "Open the appointment detail before the visit.",
          "Use patient health information only within the proper care context.",
        ],
      },
      {
        slug: "changing-appointment-state",
        title: "Changing appointment state",
        summary: "Avoid implying appointment changes until AfriMed™ saves the new state.",
        steps: [
          "Open the appointment detail.",
          "Use available status, reschedule, or cancellation actions only when enabled.",
          "Wait for the updated status to appear before treating a change as complete.",
          "Use support if the UI and appointment truth do not match.",
        ],
        callout: {
          tone: "warning",
          title: "Staging limitation",
          body: "Some appointment controls are still expanding. AfriMed™ should not imply a status change has happened until the saved appointment state updates.",
        },
      },
    ],
  },
  {
    slug: "doctor-messaging-ehr",
    title: "Messages, referrals, and EHR access",
    audience: "doctor",
    summary: "Communicate with patients and request record access only inside the right care relationship.",
    icon: "MessagesSquare",
    status: "Currently expanding",
    articles: [
      {
        slug: "messaging-patients",
        title: "Messaging patients",
        summary: "Use AfriMed™ Messages for appointment and care communication.",
        steps: [
          "Open Messages from doctor navigation.",
          "Select the patient or appointment-linked thread.",
          "Write clinically clear messages and review before sending.",
          "Use urgent care channels for emergencies instead of ordinary message threads.",
        ],
      },
      {
        slug: "requesting-ehr-access",
        questions: clinicalOutputsHelp.en.questions.slice(0,2),
        title: "Requesting EHR access",
        summary: "Ask for patient record access when the care relationship justifies it.",
        steps: [
          "Open the patient or appointment context where EHR access is available.",
          "Request access with a clear reason tied to care.",
          "Wait for approval or the available consent state before reviewing records.",
          "Use only the minimum patient information needed for care.",
        ],
        callout: {
          tone: "trust",
          title: "Consent and context",
          body: "Doctor EHR access should be tied to the right patient, appointment, consent, or care relationship.",
        },
      },
      {
        slug: "referrals",
        title: "Referrals",
        summary: "Create and review referrals without exposing patient data outside the care path.",
        steps: [
          "Open the referral action from the appointment or patient context.",
          "Check patient, receiving clinician or facility, reason, and attachments.",
          "Send only when the referral details are correct.",
          "Use support if referral visibility appears in the wrong role or account.",
        ],
      },
    ],
  },
  {
    slug: "doctor-prescriptions-notes",
    title: "Prescriptions, Fola, and SOAP notes",
    audience: "doctor",
    summary: "Use prescription and clinician-assistive tooling with professional review and accountability.",
    icon: "ClipboardList",
    status: "Currently expanding",
    articles: [
      {
        slug: "prescriptions",
        title: "Prescriptions",
        summary: "Prepare prescriptions in the correct appointment and patient context.",
        steps: [
          "Open the appointment or patient context for the prescription.",
          "Check patient identity, medicine, dose, duration, instructions, and facility context.",
          "Review the prescription before issuing it.",
          "Confirm the saved prescription status before telling the patient it is available.",
        ],
        callout: {
          tone: "critical",
          title: "Clinician responsibility",
          body: "Prescription decisions remain the responsibility of the qualified clinician.",
        },
      },
      {
        slug: "fola-for-clinicians",
        title: "Fola for clinicians",
        summary: "Use Fola as a drafting and summarization assistant, not a clinical decision maker.",
        steps: [
          "Open a supported message or appointment context.",
          "Use Fola for summaries, draft replies, or plain-language structure where available.",
          "Review every generated output before using it clinically.",
          "Do not let Fola diagnose, prescribe, or replace your professional judgement.",
        ],
        callout: {
          tone: "critical",
          title: "Review required",
          body: "Fola can support clinician workflows, but clinician review remains required for clinical notes, prescriptions, and patient communication.",
        },
      },
      {
        slug: "vitalrep-soap-notes",
        title: "VitalRep and SOAP notes",
        summary: "Understand where clinician-note support fits when available.",
        steps: [
          "Open the consultation or note context where note support is enabled.",
          "Review any generated structure against the consultation facts.",
          "Edit the note until it reflects your clinical judgement.",
          "Save only after checking patient, appointment, and note context.",
        ],
        callout: {
          tone: "info",
          title: "Currently expanding",
          body: "VitalRep and SOAP note support may not be available to every staging account yet.",
        },
      },
    ],
  },
  {
    slug: "doctor-privacy-conduct",
    title: "Privacy and professional conduct",
    audience: "doctor",
    summary: "Keep patient data, clinician identity, and practice actions inside the correct professional boundaries.",
    icon: "ShieldCheck",
    status: "Ready",
    articles: [
      {
        slug: "patient-data-boundaries",
        title: "Patient data boundaries",
        summary: "Avoid cross-role and cross-patient exposure while using AfriMed™.",
        steps: [
          "Use your doctor account only for clinician workflows.",
          "Confirm patient and appointment context before opening EHR, prescriptions, referrals, or messages.",
          "Avoid sharing screenshots that expose patient data.",
          "Report any patient data appearing in the wrong account, role, or screen.",
        ],
        callout: {
          tone: "trust",
          title: "Role-aware access",
          body: "AfriMed™ must keep patient and doctor information attached to the right authenticated role and care relationship.",
        },
      },
      {
        slug: "desktop-dashboard-use",
        title: "Desktop dashboard use",
        summary: "Use wider screens for scanning appointments, messages, settings, and clinical context.",
        steps: [
          "Open AfriMed™ on a current desktop browser.",
          "Use the doctor dashboard for appointment and workflow scanning.",
          "Open Settings for profile, verification, services, availability, and practice setup.",
          "Sign out on shared or clinic devices when finished.",
        ],
      },
    ],
  },
];

export const HELP_CATEGORIES = [
  ...PATIENT_HELP_CATEGORIES,
  ...DOCTOR_HELP_CATEGORIES,
  ...PHARMACY_HELP_CATEGORIES,
];

export const HELP_ARTICLES = HELP_CATEGORIES.flatMap((category) =>
  (category.articles || []).map((article) => ({
    ...article,
    categorySlug: category.slug,
    categoryTitle: category.title,
    audience: category.audience || "patient",
  }))
);

export function getHelpCategoriesForAudience(audience = "patient") {
  const requested = audience === "doctor" || audience === "pharmacy" ? audience : "patient";
  return HELP_CATEGORIES.filter((category) => category.audience === requested);
}

export function getHelpArticlesForAudience(audience = "patient") {
  const requested = audience === "doctor" || audience === "pharmacy" ? audience : "patient";
  return HELP_ARTICLES.filter((article) => article.audience === requested);
}

export function getHelpCategory(slug, audience = "") {
  const category = audience
    ? HELP_CATEGORIES.find((item) => item.slug === slug && item.audience === audience) || null
    : HELP_CATEGORIES.find((item) => item.slug === slug) || null;
  if (!category) return null;
  if (audience && category.audience !== audience) return null;
  return category;
}

export function getHelpArticle(categorySlug, articleSlug, audience = "") {
  const category = getHelpCategory(categorySlug, audience);
  if (!category) return null;
  const article = (category.articles || []).find((item) => item.slug === articleSlug);
  return article
    ? {
        ...article,
        categorySlug: category.slug,
        categoryTitle: category.title,
        audience: category.audience || "patient",
      }
    : null;
}

// Prefer the selected audience for duplicate slugs; a unique deep link keeps its identity.
export function resolveHelpCategory(slug, audience) {
  const matches = HELP_CATEGORIES.filter(item => item.slug === slug);
  return matches.find(item => item.audience === audience) || (matches.length === 1 ? matches[0] : null);
}
export function resolveHelpArticle(categorySlug, articleSlug, audience) {
  const matches = HELP_ARTICLES.filter(item => item.categorySlug === categorySlug && item.slug === articleSlug);
  return matches.find(item => item.audience === audience) || (matches.length === 1 ? matches[0] : null);
}
